import { HttpClient } from "@angular/common/http";
import { Injectable } from "@angular/core";
import { Observable } from "rxjs";
import { BaseService } from "../base/base-service";
import { API_CONSTANTS } from "../base/api-constants";

/** Post-payment process order for any package purchase. */
export interface PackageProcessOrder {
  id: number;
  packageId: number;
  processStatus: number;
  processStatusLabelAr: string;
  processStatusLabelEn?: string;
  orderDate: string;
  submissionDate: string | null;
  packageDefinitionCode?: string;
  packageDefinitionDisplayName?: string;
  hasCv?: boolean;
  hasLinkedIn?: boolean;
  hasMockInterview?: boolean;
  canOpenSuccessPage: boolean;
  canSubmit: boolean;
  slaStarted: boolean;
  slaOverdue?: boolean;
  prefill?: {
    arabicName?: string;
    englishName?: string;
    serviceEmail?: string;
    whatsAppNumber?: string;
    currentCity?: string;
    /** Internal media key — use only with MediaService / View CV. */
    resumePath?: string;
    resumeFileName?: string;
    hasProfileResume?: boolean;
  };
}

export type PackageProcessCvSource = 'profile' | 'upload' | 'none';

export const PACKAGE_PROCESS_STATUS = {
  AwaitingDataSubmission: 0,
  InProgress: 1,
  Completed: 2,
} as const;

/** Session flag: Profile Completion entered via post-pay package process. */
export const PACKAGE_PROCESS_CONTEXT_KEY = 'package_process_context';

@Injectable({
  providedIn: "root",
})
export class PackageService extends BaseService {
  constructor(private httpClient: HttpClient) {
    super(httpClient);
  }

  Checkout(packageDefinitionId: number): Observable<any> {
    return this.get(API_CONSTANTS.Packages.CheckoutPackage + packageDefinitionId);
  }

  CheckoutWithBilling(packageDefinitionId: number, billing?: {
    firstName?: string;
    lastName?: string;
    email?: string;
    phoneNumber?: string;
    country?: string;
    state?: string;
    city?: string;
    street?: string;
    building?: string;
    apartment?: string;
    postalCode?: string;
  }, couponCode?: string | null): Observable<any> {
    const body = { packageDefinitionId, billing: billing || null, couponCode: couponCode || null };
    return this.post(API_CONSTANTS.Packages.CheckoutPackagePost, body);
  }
  PreviewCheckout(packageDefinitionId: number, couponCode?: string | null): Observable<any> {
    let url = API_CONSTANTS.Packages.PreviewCheckout + packageDefinitionId;
    if (couponCode) {
      url += `?couponCode=${encodeURIComponent(couponCode)}`;
    }
    return this.get(url);
  }
  GetPackage(id: number): Observable<any> {
    return this.get(API_CONSTANTS.Packages.GetPackage + id);
  }
  GetPackagePrices(): Observable<any> {
    return this.get(API_CONSTANTS.Packages.GetPackagePrices);
  }
  RequestCVPackage(model: any): Observable<any> {
    return this.post_formData(API_CONSTANTS.Packages.RequestCVPackage, model);
  }
  RequestLinkedInPackage(model: any): Observable<any> {
    return this.post_formData(
      API_CONSTANTS.Packages.RequestLinkedInPackage,
      model,
    );
  }
  GetActivePackage(): Observable<any> {
    return this.get(API_CONSTANTS.Packages.GetActivePackage);
  }

  UploadMockInterviewResume(model: FormData): Observable<any> {
    return this.post_formData(API_CONSTANTS.Packages.UploadMockInterviewResume, model);
  }

  /** Post-pay process order for the current user (optional deep-link). */
  getMyProcessOrder(orderId?: number | null, packageId?: number | null): Observable<any> {
    let url = API_CONSTANTS.Packages.MyProcessOrder;
    const params: string[] = [];
    if (orderId != null && orderId > 0) params.push(`orderId=${orderId}`);
    if (packageId != null && packageId > 0) params.push(`packageId=${packageId}`);
    if (params.length) url += `?${params.join('&')}`;
    return this.get(url);
  }

  /** Upload a CV for the open process order (order-scoped; does not change profile resume). */
  uploadProcessResume(formData: FormData): Observable<any> {
    return this.post_formData(API_CONSTANTS.Packages.UploadProcessResume, formData);
  }

  submitProcess(body: Record<string, unknown>): Observable<any> {
    return this.post(API_CONSTANTS.Packages.SubmitProcess, body);
  }

  setProcessContext(): void {
    sessionStorage.setItem(PACKAGE_PROCESS_CONTEXT_KEY, '1');
  }

  clearProcessContext(): void {
    sessionStorage.removeItem(PACKAGE_PROCESS_CONTEXT_KEY);
  }

  hasProcessContext(): boolean {
    return sessionStorage.getItem(PACKAGE_PROCESS_CONTEXT_KEY) === '1';
  }
}
